import { useCookieInteraction } from "@jmc/core/src/hooks/useCookieInteraction/index";
import { WarningMessage } from "@jmc/solid-design-system/src/components/atoms/WarningMessage/WarningMessage";
import { CookieConsent } from "@jmc/solid-design-system/src/utils/cookieConsent";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import React from "react";
import { useTranslation } from "react-i18next";
import yn from "yn";

type Props = {
    children?: JSX.Element;
    variant?: "banner" | "card" | "small";
    functional?: boolean;
    targeting?: boolean;
    media?: boolean;
};

export const CookieMessage = ({
    children,
    variant = "banner",
    functional = true,
    targeting = false,
    media,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const { accepted } = useCookieInteraction();
    const { jnjFullBranded } = useJnjBranding();

    const acceptedCookies = accepted
        ?.split(",")
        ?.filter(Boolean)
        ?.map((cookie) => Number(cookie));

    const hasAcceptedCookies = (): boolean => {
        if (!acceptedCookies) return false;
        if (typeof navigator !== `undefined`) {
            if (!navigator.cookieEnabled) return false;
        } else {
            // during SSR, navigator is undefined and we would prefer not to prerender the cookie message
            return true;
        }
        if (functional && !acceptedCookies.includes(CookieConsent.FUNTIONAL_COOKIES)) return false;
        if (targeting && !acceptedCookies.includes(CookieConsent.TARGETING_COOKIES)) return false;
        if (media && !acceptedCookies.includes(CookieConsent.MEDIA_COOKIES)) return false;
        return true;
    };

    if (hasAcceptedCookies() || yn(process.env.GATSBY_DISABLE_COOKIEBANNER)) return children;

    return (
        <WarningMessage
            warning={t(
                "common:It seems your browser is blocking 3rd party cookies which are required to display videos. To resolve this issue, please update your cookie settings to allow these 3rd party cookies.",
            )}
            cookieMessage={t("Manage cookie settings", { ns: "common" })}
            variant={variant}
            textSpacing={jnjFullBranded}
        />
    );
};

export default CookieMessage;
